import React, { useEffect, useState } from 'react'
import { useGroupedShortenedLabels } from '../hooks/use-grouped-shortened-labels'
import { useMediaQuery, useTheme } from '@mui/material'
import { Breadcrumb } from '@e3dc-react/shell/Elements/Header/Breadcrumbs'
import { BreadcrumbsLayout, useBreadcrumbStyles } from './BreadcrumbsLayout'
import { getItemName, goToDirectParent } from '../utils/breadcrumb-utils'
import { BreadcrumbItem } from '../models'

interface Props {
  parentCategories: BreadcrumbItem[]
  onClickCategory: (id: number) => void
  onClickBackButton?: () => void
}

/**
 * Breadcrumbs component that groups breadcrumb titles in a sub menu if they exceed available space
 */
const CategoryBreadcrumbs: React.FC<Props> = ({ parentCategories, onClickCategory, onClickBackButton }) => {
  const { classes } = useBreadcrumbStyles()
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([])
  const theme = useTheme()
  const isXs = useMediaQuery(theme.breakpoints.only('xs'))
  const variant = isXs ? 'h4' : 'h2'

  const iconButtonRef = React.useRef<HTMLDivElement>(null)
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const dotRef = React.useRef<HTMLDivElement>(null)

  const filteredParentCategories = isXs ? parentCategories.slice(0, -1) : parentCategories

  const { titles: shortenTitles, menuItems } = useGroupedShortenedLabels({
    parentCategories: filteredParentCategories,
    iconButtonRef,
    wrapperRef,
    dotRef,
    variant,
    breakLastBreadcrumb: isXs,
    contentSpacer: theme.space(1),
  })

  const createMenuChildren = (idx: number): Breadcrumb[] | undefined => {
    if (idx !== 0 || menuItems.length === 0) {
      return undefined
    }
    return menuItems.map(menuItem => ({
      title: getItemName(menuItem.id, menuItem.name),
      onClick: () => onClickCategory(menuItem.id),
    }))
  }

  useEffect(() => {
    const newBreadcrumbs = shortenTitles.map((item, idx) => ({
      title: item.name,
      onClick: shortenTitles.length - 1 === idx && !isXs ? undefined : () => onClickCategory(item.id),
      className: shortenTitles.length - 1 === idx && !isXs ? classes.lastBreadcrumbItem : classes.breadcrumbItem,
      children: createMenuChildren(idx),
    }))

    setBreadcrumbs(newBreadcrumbs)
  }, [shortenTitles, menuItems, parentCategories]) // eslint-disable-line react-hooks/exhaustive-deps

  if (parentCategories.length === 0) {
    return null
  }

  return (
    <BreadcrumbsLayout
      iconButtonRef={iconButtonRef}
      wrapperRef={wrapperRef}
      dotRef={dotRef}
      onGoBack={() => goToDirectParent(parentCategories, onClickCategory, onClickBackButton)}
      breadcrumbs={breadcrumbs}
      variant={variant}
      breakLastBreadcrumb={isXs}
    />
  )
}

export default CategoryBreadcrumbs
